import { Popover } from '@bambi/component-library/src/components/Popover/Popover';
import { BellIcon } from '@radix-ui/react-icons';

import useTripSubscriptsExpiringSoonCount from 'src/features/trip/trip-subscriptions/useTripSubscriptsExpiringSoonCount';

import useCredentialsExpiringSoonCount from '../../credentials/useCredentialsExpringSoonCount';
import { NotificationItem } from './NotificationItem';

export default function Notifications() {
  const expiredCredentials = useCredentialsExpiringSoonCount();
  const expiredTripSubscriptions = useTripSubscriptsExpiringSoonCount();

  const pluralize = (count: number, word: string) =>
    `${word}${count === 1 ? '' : 's'}`;

  const notificationsConfig = [
    {
      type: 'team',
      count: expiredCredentials?.team || 0,
      title: 'Member Credentials',
      description: (count: number) =>
        `${count} member ${pluralize(count, 'credential')} expiring soon`,
      href: '/team/member-credentials',
    },
    {
      type: 'trip-subscription',
      count: expiredTripSubscriptions || 0,
      title: 'Trip Subscriptions',
      description: (count: number) =>
        `${count} trip ${pluralize(count, 'subscription')} expiring soon`,
      href: '/trips/subscriptions',
    },
    {
      type: 'vehicle',
      count: expiredCredentials?.vehicle || 0,
      title: 'Vehicle Credentials',
      description: (count: number) =>
        `${count} vehicle ${pluralize(count, 'credential')} expiring soon`,
      href: '/fleet/credentials',
    },
  ];

  const notificationCount = notificationsConfig.reduce(
    (sum, notification) => sum + notification.count,
    0
  );

  return (
    <div className="relative">
      <Popover
        content={
          <div
            className="-m-[10px] min-w-[350px]"
            role="dialog"
            aria-label="Notifications"
          >
            {notificationCount > 0 ? (
              <div
                data-testid="notifications-list"
                className="divide-y divide-gray-200 [&>*]:py-2"
              >
                {notificationsConfig.map(
                  (notification) =>
                    notification.count > 0 && (
                      <NotificationItem
                        key={notification.type}
                        title={notification.title}
                        description={notification.description(
                          notification.count
                        )}
                        href={notification.href}
                      />
                    )
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 py-2">
                <div data-testid="no-notifications">
                  <h1>No notifications. Please check back later.</h1>
                </div>
              </div>
            )}
          </div>
        }
        trigger={
          <button
            type="button"
            className="focus:ring-indigo-500 rounded-full p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
            data-testid="notifications-trigger"
            aria-label={`${notificationCount} notifications`}
          >
            <span className="sr-only">View notifications</span>
            <BellIcon
              data-testid="bell-icon"
              className="h-6 w-6"
              aria-hidden="true"
            />
            {notificationCount > 0 && (
              <span
                className="absolute right-[-8px] top-[-8px] rounded-full bg-red-600 px-2 py-1 text-xs text-white"
                role="status"
                aria-live="polite"
                data-testid="notification-count"
              >
                {notificationCount}
              </span>
            )}
          </button>
        }
      />
    </div>
  );
}
